/**
 * User Management Types
 * Matches: security.users, security.user_directory, tenant users tables
 */

// ============================================================================
// USER ENUMS
// ============================================================================

import {
  ROLE_HIERARCHY,
  ROLE_PERMISSION_SUMMARIES,
  getAssignableRolesFor,
  getRoleDescriptionFromContract,
  getRoleLabel,
  resolveRole,
} from '@/security/accessContract'
import type { UserRole } from '@/security/accessContract'

export type { CanonicalUserRole, UserRole } from '@/security/accessContract'
export { ROLE_HIERARCHY }

/**
 * User Status
 */
export type UserStatus = 'active' | 'inactive' | 'pending' | 'locked' | 'unknown'

/**
 * User Category
 * - system: Platform administrators
 * - ca_staff: CA firm internal users
 * - client: External clients
 */
export type UserCategory = 'system' | 'ca_staff' | 'client'

// ============================================================================
// USER INTERFACES
// ============================================================================

/**
 * User
 * Matches: security.users + tenant users table
 */
export interface User {
  userId: string

  // Authentication
  email: string
  username?: string

  // Profile
  fullName: string
  firstName?: string
  lastName?: string
  phone?: string
  avatarUrl?: string

  // Tenant association
  tenantId: string
  tenantName?: string

  // Role & Status
  role: UserRole
  userCategory: UserCategory
  isActive: boolean | null
  isVerified: boolean | null

  // Security
  failedLogins: number | null
  lockedUntil?: string
  lastLoginAt?: string

  // Invitation provenance (populated when the user originated from an invite)
  invitedAt?: string
  invitedByName?: string

  // Preferences
  preferences: UserPreferences

  // Timestamps
  createdAt: string | null
  updatedAt: string | null
  deletedAt?: string
}

export interface UserPermissionOverrides {
  granted?: string[]
  revoked?: string[]
}

/**
 * User Preferences
 */
export interface UserPreferences {
  theme?: 'light' | 'dark' | 'system'
  language?: string
  timezone?: string
  notifications?: {
    email: boolean
    push: boolean
    sms: boolean
  }
  dashboardLayout?: Record<string, unknown>
  permissionOverrides?: UserPermissionOverrides
}

/**
 * User Directory Entry (lightweight lookup)
 * Matches: security.user_directory table
 */
export interface UserDirectoryEntry {
  userId: string
  tenantId: string
  email: string
  username?: string
  isActive: boolean
  fullName?: string
  role?: string
  createdAt: string | null
  updatedAt: string | null
}

// ============================================================================
// USER FILTERS
// ============================================================================

/**
 * User Filters for list view
 */
export interface UserFilters {
  role?: UserRole | 'all'
  status?: UserStatus | 'all'
  isVerified?: boolean | 'all'
  searchQuery?: string
}

/**
 * Default user filters
 */
export const DEFAULT_USER_FILTERS: UserFilters = {
  role: 'all',
  status: 'all',
  isVerified: 'all',
  searchQuery: '',
}

// ============================================================================
// INPUT TYPES
// ============================================================================

/**
 * Create User Input
 */
export interface CreateUserInput {
  email: string
  fullName: string
  phone?: string
  role: UserRole
  /**
   * Temporary password for the new account. Required in real mode (the
   * security-service create endpoint mandates it); ignored by the mock service.
   */
  password?: string
  sendInvite?: boolean
}

/**
 * Invite User Input
 */
export interface InviteUserInput {
  email: string
  fullName?: string
  role: UserRole
  message?: string
}

/**
 * Update User Input
 */
export interface UpdateUserInput {
  fullName?: string
  phone?: string
  avatarUrl?: string
  role?: UserRole
  isActive?: boolean
  preferences?: Partial<UserPreferences>
}

/**
 * Change Password Input
 */
export interface ChangePasswordInput {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

// ============================================================================
// INVITATION TYPES
// ============================================================================

/**
 * Invitation Status
 */
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled'

/**
 * User Invitation
 */
export interface UserInvitation {
  invitationId: string
  tenantId: string
  email: string
  fullName?: string
  role: UserRole
  status: InvitationStatus
  invitedBy: string
  invitedByName?: string
  message?: string
  token: string
  expiresAt: string
  acceptedAt?: string
  createdAt: string
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get role display name
 */
export function getRoleDisplayName(role: UserRole): string {
  return getRoleLabel(role)
}

/**
 * Get role description
 */
export function getRoleDescription(role: UserRole): string {
  return getRoleDescriptionFromContract(role)
}

/**
 * Get user status display name
 */
export function getUserStatusDisplayName(status: UserStatus): string {
  switch (status) {
    case 'active':
      return 'Active'
    case 'inactive':
      return 'Inactive'
    case 'pending':
      return 'Pending Verification'
    case 'locked':
      return 'Locked'
    case 'unknown':
      return 'Not recorded'
    default:
      return 'Unknown'
  }
}

/**
 * Get user status badge color
 */
export function getUserStatusColor(status: UserStatus): string {
  switch (status) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'warning'
    case 'pending':
      return 'info'
    case 'locked':
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(role: UserRole): string {
  switch (resolveRole(role).canonicalRole) {
    case 'firm_owner':
      return 'primary'
    case 'firm_admin':
      return 'secondary'
    case 'ca_partner':
      return 'accent'
    case 'technical_assistant':
      return 'info'
    case 'research_assistant':
      return 'warning'
    case 'individual_client':
    case 'business_owner_client':
    case 'authorized_client':
      return 'ghost'
    default:
      return 'default'
  }
}

/**
 * Get invitation status color
 */
export function getInvitationStatusColor(status: InvitationStatus): string {
  switch (status) {
    case 'pending':
      return 'warning'
    case 'accepted':
      return 'success'
    case 'expired':
      return 'error'
    case 'cancelled':
      return 'default'
    default:
      return 'default'
  }
}

/**
 * Check if role can manage other users
 */
export function canManageUsers(role: UserRole): boolean {
  const canonical = resolveRole(role).canonicalRole
  return canonical === 'firm_owner' || canonical === 'firm_admin'
}

/**
 * Check if role can invite users
 */
export function canInviteUsers(role: UserRole): boolean {
  return canManageUsers(role)
}

/**
 * Get roles that a user can assign based on their own role
 */
export function getAssignableRoles(currentUserRole: UserRole): UserRole[] {
  return getAssignableRolesFor(currentUserRole)
}

/**
 * Role permissions summary for display
 */
export const ROLE_PERMISSIONS_SUMMARY = ROLE_PERMISSION_SUMMARIES

/**
 * Check if roleA has higher privilege than roleB
 */
export function hasHigherPrivilege(roleA: UserRole, roleB: UserRole): boolean {
  return ROLE_HIERARCHY[roleA] > ROLE_HIERARCHY[roleB]
}

/**
 * Check if roleA can modify roleB
 */
export function canModifyRole(currentRole: UserRole, targetRole: UserRole): boolean {
  return ROLE_HIERARCHY[currentRole] > ROLE_HIERARCHY[targetRole]
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Email format validation
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Phone format validation (Indian)
 */
export const PHONE_REGEX = /^[6-9][0-9]{9}$/

/**
 * Password validation (min 8 chars, uppercase, lowercase, digit)
 */
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/

/**
 * Validate email
 */
export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

/**
 * Validate phone
 */
export function isValidPhone(phone: string): boolean {
  return PHONE_REGEX.test(phone)
}

/**
 * Validate password strength
 */
export function isValidPassword(password: string): boolean {
  return PASSWORD_REGEX.test(password)
}

/**
 * Get password strength feedback
 */
export function getPasswordStrength(password: string): {
  score: number
  feedback: string[]
} {
  const feedback: string[] = []
  let score = 0

  if (password.length >= 8) score += 1
  else feedback.push('At least 8 characters')

  if (/[a-z]/.test(password)) score += 1
  else feedback.push('At least one lowercase letter')

  if (/[A-Z]/.test(password)) score += 1
  else feedback.push('At least one uppercase letter')

  if (/\d/.test(password)) score += 1
  else feedback.push('At least one digit')

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1
  else feedback.push('Special character recommended')

  return { score, feedback }
}
